import { Elements } from '@stripe/react-stripe-js';
import WhatsappApiProvider from 'src/pages/WhatsappApi/WhatsappApi.provider.tsx';
import TeamProvider from 'src/pages/Settings/components/Team/Team.provider';
import SubscriptionManagerProvider from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.provider';
import { STRIPE_ELEMENTS_CONFIG } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.constants';
import { stripePromise } from 'src/constants/stripe.constants';
import ACLProvider from './ACLProvider/ACL.provider.tsx';
import IntegrationsProvider from './IntegrationsProvider/Integrations.provider';
import { LayoutProvider } from './LayoutProvider/Layout.provider';
import WorkspaceAgentProvider from './WorkspaceAgentProvider/Workspace.provider';
import { tProvidersProps } from './LayoutProvider/Layout.types';
import { ModalsProvider } from './ModalsProvider/Modals.provider';
import { NotificationsProvider } from './NotificationProvider/Notification.provider.tsx';
import { SocketsProvider } from './SocketsProvider/Socket.provider';
import SubscriptionProvider from './SubscriptionProvider/Subscription.provider';
import SyncConversationsProvider from './SyncConversationsProvider/SyncConversations.provider.tsx';
import ToastProvider from './ToastProvider/Toast.provider';
import GlobalBannerProvider from './GlobalBannerProvider/GlobalBanner.provider';
import UserProvider from './UserProvider/User.provider';
import WorkspaceProvider from './WorkspaceProvider/Workspace.provider';
import FeatureFlagsProvider from './FeatureFlagsProvider/FeatureFlags.provider';
import ProfilingProgressProvider from './ProfilingProgressProvider/ProfilingProgress.provider';
import FunnelCacheInvalidationProvider from 'src/providers/FunnelCacheProvider/FunnelCache.provider';
import IntercomProvider from './IntercomProvider/Intercom.provider.tsx';
import BirdieProvider from './BirdieProvider/Birdie.provider';
import LeadAgentAcceptanceProvider from './LeadAgentAcceptanceProvider/LeadAgentAcceptance.provider';
import AlertCenterBoundary from './AlertCenterProvider/AlertCenterBoundary.component';
import WalletProvider from 'src/pages/Wallet/Wallet.provider';
import LeadCloseProvider from 'src/components/LeadClose/LeadClose.provider';

export default function Providers(props: tProvidersProps) {
  const { children } = props;

  return (
    <ToastProvider>
      <GlobalBannerProvider>
        <UserProvider>
          <WorkspaceProvider>
            <SubscriptionProvider>
              <ACLProvider>
                {/*
                  RET-1230 — LeadAgentAcceptanceProvider sits inside ACLProvider
                  because it only fetches for Owners (userIsOwner gate). It has
                  no socket dependency; the paywall banner + modals consume it
                  from LayoutProvider templates downstream.
                */}
                <LeadAgentAcceptanceProvider>
                  <SocketsProvider>
                    {/*
                    INT-945 — FunnelCacheInvalidationProvider must mount inside
                    SocketsProvider so it can subscribe to funnelUpdatedEvent;
                    it invalidates the `funnelData` localStorage cache globally
                    so newly-invited users appear in the assign-to dropdown
                    without requiring a page reload or logout/login.
                  */}
                    <FunnelCacheInvalidationProvider>
                      {/*
                      FeatureFlagsProvider sits below SocketsProvider so it can
                      subscribe to workspaceUpdatedEvent and refetch derived
                      flags (RET-1027) when the workspace plan changes mid-session.
                      Consumers of useFeatureFlagsContext (AuthMiddleware,
                      LeadInfo, MessageBubbleV2, Record) all render below this.
                    */}
                      <FeatureFlagsProvider>
                        {/*
                          ProfilingProgressProvider owns the single instance of
                          the sync/profiling state machine, so one socket
                          message drives one state transition and one poll set
                          however many surfaces read it — the navbar indicator,
                          the sync toast and the pipe overlay each instantiated
                          their own before. It sits inside SocketsProvider (its
                          events), WorkspaceProvider (the workspace-scoped
                          status endpoints) and FeatureFlagsProvider (the
                          `leads_auto-profiling` gate).
                        */}
                        <ProfilingProgressProvider>
                          {/*
                          RET-1763 — WalletProvider sits inside FeatureFlagsProvider
                          because it gates every fetch on `billing_wapi-wallet`: an
                          unflagged workspace must generate zero wallet traffic. It
                          also needs WorkspaceProvider (workspace id) and
                          SocketsProvider (workspaceUpdatedEvent), both above.
                          Global rather than page-scoped because the balance is read
                          far outside the wallet screen — estimated cost in campaigns
                          and 1-to-1 chat, the low-balance banner, and from R2/R3 the
                          gate that blocks sending when the balance runs out.
                        */}
                          <WalletProvider>
                            {/*
                              LEA-50 — LeadCloseProvider, like WalletProvider, sits
                              inside FeatureFlagsProvider and makes no request without
                              `leads_lead-close`. Global because the chat bar, the lead
                              panel, Historial and the board all open the same form.
                            */}
                            <LeadCloseProvider>
                              <NotificationsProvider>
                                <SyncConversationsProvider>
                                  <IntegrationsProvider>
                                    <ModalsProvider>
                                      {/* TeamProvider itself calls useStripe for seat purchases. */}
                                      <Elements
                                        stripe={stripePromise}
                                        options={STRIPE_ELEMENTS_CONFIG}
                                      >
                                        <TeamProvider>
                                          <WhatsappApiProvider>
                                            <IntercomProvider>
                                              {/*
                                            AlertCenterProvider sits inside
                                            WhatsappApiProvider (account health)
                                            and IntercomProvider (the `intercom`
                                            CTA kind), which are the lowest of
                                            the contexts its snapshot reads —
                                            ACL, Subscription, Workspace, User,
                                            Sockets, LeadAgentAcceptance,
                                            Integrations and Team are all above.
                                            It stays above LayoutProvider so
                                            every private surface can consume it.
                                            Distinct from NotificationsProvider,
                                            which owns agenda toasts.

                                            RET-1871 — wrapped in
                                            AlertCenterBoundary (Tier 1 of the
                                            two-tier containment): a throw during
                                            snapshot assembly happens ABOVE every
                                            alert-consuming surface, so only a
                                            boundary here — not one around the
                                            consumers — can catch it. On catch,
                                            the whole private tree below this
                                            point is REMOUNTED with an empty
                                            alert context; accepted tradeoff over
                                            a full crash of the private shell.
                                          */}
                                              <AlertCenterBoundary>
                                                <BirdieProvider>
                                                  {/*
                                                   * GRO-811 — `<SubscriptionManagerProvider>` is
                                                   * lifted here so the AddPaymentMethod modal (and any other
                                                   * SubscriptionsManager-driven modal) is reachable from any
                                                   * route, not only from `/settings`. The hook depends on
                                                   * Workspace/Subscription/ACL so it sits below those, and
                                                   * `useStripe()/useElements()` calls inside the hook require
                                                   * `<Elements>` to be in scope above TeamProvider.
                                                   */}

                                                  <SubscriptionManagerProvider>
                                                    <WorkspaceAgentProvider>
                                                      <LayoutProvider>
                                                        {children}
                                                      </LayoutProvider>
                                                    </WorkspaceAgentProvider>
                                                  </SubscriptionManagerProvider>
                                                </BirdieProvider>
                                              </AlertCenterBoundary>
                                            </IntercomProvider>
                                          </WhatsappApiProvider>
                                        </TeamProvider>
                                      </Elements>
                                    </ModalsProvider>
                                  </IntegrationsProvider>
                                </SyncConversationsProvider>
                              </NotificationsProvider>
                            </LeadCloseProvider>
                          </WalletProvider>
                        </ProfilingProgressProvider>
                      </FeatureFlagsProvider>
                    </FunnelCacheInvalidationProvider>
                  </SocketsProvider>
                </LeadAgentAcceptanceProvider>
              </ACLProvider>
            </SubscriptionProvider>
          </WorkspaceProvider>
        </UserProvider>
      </GlobalBannerProvider>
    </ToastProvider>
  );
}
